import { FC, useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';

type JsonEditorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: unknown) => void;
  initialData: unknown;
  title?: string;
  isSaving?: boolean;
};

const JsonEditorModal: FC<JsonEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  title = 'Edit JSON',
  isSaving = false,
}) => {
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      try {
        const formatted = initialData ? JSON.stringify(initialData, null, 2) : '{}';
        setJsonText(formatted);
        setError(null);
      } catch {
        setJsonText('{}');
        setError(null);
      }
    }
  }, [initialData, isOpen]);

  const handleTextChange = (value: string) => {
    setJsonText(value);
    try {
      JSON.parse(value);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  const handleSave = () => {
    try {
      const parsed = JSON.parse(jsonText);
      onSave(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4 my-8 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden px-6 py-4 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">JSON Editor</span>
            <button
              onClick={handleFormat}
              className="text-sm text-[#C87941] hover:text-[#a5612f] font-medium"
            >
              Format JSON
            </button>
          </div>
          <textarea
            value={jsonText}
            onChange={(e) => handleTextChange(e.target.value)}
            className={`flex-1 min-h-[400px] w-full px-4 py-3 font-mono text-sm border rounded-lg resize-none focus:ring-2 focus:ring-[#C87941] focus:border-transparent ${
              error ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            spellCheck={false}
          />
          {error && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <LoadingButton
            loading={isSaving}
            onClick={handleSave}
            disabled={!!error}
            className="bg-[#0f172a] hover:bg-[#1e293b] text-white border-none px-6 disabled:opacity-50"
          >
            Save
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

export default JsonEditorModal;
